/**
 * BrownVideoChat - Chat interface for querying Brown Bears basketball data
 * Uses Gemini API with EXCLUSIVELY Brown Bears context (no Real Madrid/Barcelona data)
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { NODE_BACKEND_URL } from '../../utils/constant';
import { marked } from 'marked';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Markdown renderer
const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

// Actual video analysis documents from /brown folder
const BROWN_VIDEO_ANALYSIS_DOCS = `
## DETAILED PLAY-BY-PLAY VIDEO ANALYSIS

The following are detailed breakdowns of every possession in the Brown Offensive Breakdowns video.

---

### PLAY 1 - [00:09] High Pick & Roll
**Initiator:** #1 Isaiah Langham (Brown)
**Action Type:** High Pick & Roll

**What Happened:**
Langham calls for a high ball screen from #45 (Dabo). He attacks the right elbow aggressively, drawing the help defense inward. Recognizing the defensive collapse, he threads a precise pocket bounce pass to Dabo, who is rolling hard to the rim. Dabo gathers and finishes through contact.

**Defensive Response:**
**Hard Hedge / Blitz.** The Penn big man (#14) steps out hard to force the ball handler wide but is slow to recover. The tag defender from the weak side is late to obstruct the roller's lane.

**Result:** Made Layup (#45 Dabo).

**Coaching Note:**
Excellent patience by the ball handler to string out the hedge and wait for the passing lane to open up for the roller.

---

### PLAY 2 - [00:15] High Pick & Roll
**Initiator:** #3 Jeremah Jenkins (Brown)
**Action Type:** High Pick & Roll

**What Happened:**
Jenkins uses a high ball screen to attack the right side. He reads the aggressive positioning of the opposing big man and delivers a precise pocket bounce pass to the rolling big man (#45 Dabo). The roller catches on the short roll, takes one power dribble to compose himself, and finishes at the rim.

**Defensive Response:**
**Hard Hedge / Blitz.** The Penn big man hedges hard to force the ball handler out, but stays too high for too long. The on-ball defender fights over the screen but cannot disrupt the passing lane. The weakside help defender (#11) stays attached to the corner shooter, leaving the paint exposed.

**Result:** Made layup (Dabo).

**Coaching Note:**
The guard's patience to string out the hedge created the angle; the weakside "tagger" must rotate earlier to bump the roller when the ball is centered.

---

### PLAY 3 - [00:22] Transition Drag Screen / Alley-Oop
**Initiator:** #3 Jeremah Jenkins
**Action Type:** Transition Drag Screen

**What Happened:**
In semi-transition, Jenkins utilizes a drag screen at the top of the key. Recognizing the defensive big is dropping deep into the paint to protect the rim, Jenkins attacks downhill aggressively to freeze the defender, then lofts an alley-oop to Dabo who rolls hard behind the defense.

**Defensive Response:**
**Deep Drop.** The defensive big (#14) drops all the way to the restricted area, giving the guard too much space to operate. He fails to get a hand up to deter the pass or body the roller.

**Result:** Made Dunk (Alley-oop to Dabo).

**Coaching Note:**
Drop coverage against a dynamic guard requires active hands; the defender was too passive and got caught in "no man's land" between the ball and the lob threat.

---

### PLAY 4 - [00:29] Screen Rejection / Baseline Attack
**Initiator:** #10 Kino Lilly Jr. (Brown)
**Action Type:** Pick & Roll / Rejecting the Screen

**What Happened:**
The ball handler sees his defender jumping to the screen side early to fight over it. He immediately rejects the screen, driving hard to the left (baseline side). This creates a 2-on-1 situation in the lane, forcing the help defense to commit to the ball, leaving the "screener" (who slipped to the rim) open for a dump-off pass.

**Defensive Response:**
**Ice / Side.** The on-ball defender tried to force the ball handler into the screen but got beat on the rejection. The help rotation from the baseline was late.

**Result:** Made Layup.

**Coaching Note:**
Great read to reject the screen; when the defender cheats the alignment, attack the open lane immediately rather than forcing the set play.

---

### PLAY 5 - [01:03] High Pick & Roll vs Blitz
**Initiator:** #10 Kino Lilly Jr.
**Action Type:** High Pick & Roll vs Blitz

**What Happened:**
The defense aggressively double-teams (blitzes) the ball handler off the screen to get the ball out of his hands. The guard stays calm, retreats slightly to improve the passing angle, and hits the short-rolling big man in the middle of the floor (the "release valve"). The big man catches and makes a play 4-on-3.

**Defensive Response:**
**Blitz / Trap.** Both defenders committed to the ball handler. The breakdown occurred behind the play where the rotations were too slow to cover the rolling big man in the middle of the paint.

**Result:** Made Layup (Assist to finish).

**Coaching Note:**
Against a blitz, the short roll is the most dangerous weapon; the big must flash to open space quickly to punish the aggression.

---

### PLAY 6 - [01:28] Clutch Spread High Pick & Roll
**Initiator:** #3 Jeremah Jenkins
**Action Type:** Spread High Pick & Roll

**What Happened:**
In a crucial late-game moment (1-point game), Jenkins calls for a high screen. With the floor spaced (shooters in corners), he attacks the drop coverage. The defender stays low, and Jenkins throws a perfect lob over the top to Dabo. The timing coincides with the crowd noise swelling, indicating a high-leverage play.

**Defensive Response:**
**Drop Coverage.** The defender (#33) is worried about the drive and backpedals, failing to physically tag the roller. The weakside defenders are glued to shooters, offering no help at the rim.

**Result:** Made Dunk (Alley-oop).

**Coaching Note:**
In clutch situations, vertical spacing (the lob) is as valuable as horizontal spacing; the defense must have a designated low-man rotation to stop this easy bucket.

---

### PLAY 7 - [05:32] Pick & Roll Snake vs Providence
**Initiator:** #10 Kino Lilly Jr.
**Action Type:** Pick & Roll / Kick & Roll

**What Happened:**
Late in the shot clock against a Big East opponent (Providence), the guard uses a screen and snakes/refuses slightly to keep the defender on his hip. He draws the big defender up and slips a pocket pass to the roller (#23) who finishes through contact.

**Defensive Response:**
**Drop.** The Providence big man is caught flat-footed. He steps up too late to stop the ball and is out of position to recover to the roller.

**Result:** Made Layup (And-One possibility).

**Coaching Note:**
The guard's change of pace kept the big defender guessing; decisive passing in tight windows is critical against high-major length.

---

### PLAY 8 - [06:50] Dribble Handoff (DHO)
**Initiator:** #3 Jeremah Jenkins (Brown)
**Action Type:** Dribble Handoff (DHO)

**What Happened:**
Jenkins passes to the big (#31) at the top of the key and immediately sprints into a Dribble Handoff. He curls tightly around the big man, turning the corner and forcing the defensive big to step up. This creates a passing window for a pocket pass to #31, who rolls freely to the rim for a layup.

**Defensive Response:**
**Drop Coverage.** The defender on the ball trails over the screen, and the big defender steps up to contain the ball but leaves the roller unaccounted for.

**Result:** Made Layup (#31).

**Coaching Note:**
Curling tight off the DHO shoulder is crucial; it prevents the trailing defender from shooting the gap and forces the big to commit to the ball handler.

---

### PLAY 9 - [07:34] Chicago Action (Pindown into DHO)
**Initiator:** #10 Kino Lilly Jr. (Brown)
**Action Type:** Chicago Action (Pindown into DHO)

**What Happened:**
The play starts with #10 coming off a pindown screen on the wing, which flows directly into a Dribble Handoff with the big (#31) at the top of the key. The momentum from the initial screen allows #10 to blow past his defender who is trailing. He drives into the paint and drops a bounce pass to the rolling big man.

**Defensive Response:**
**Trailing.** The defender is forced to chase over the top of both screens, putting him out of position to contest the drive or the pass.

**Result:** Made Layup (#31).

**Coaching Note:**
Using an off-ball screen *before* the on-ball action (Chicago action) puts the defender in a trail position, making the DHO significantly harder to guard.

---

### PLAY 10 - [09:21] Pull-Up Three vs Under Coverage
**Initiator:** #3 Jeremah Jenkins (Brown)
**Action Type:** High Pick & Roll

**What Happened:**
Jenkins brings the ball up and calls for a high ball screen. He recognizes that his defender is cheating by going *under* the screen to beat him to the spot. Instead of driving, Jenkins uses the screen to create separation and immediately pulls up for a rhythm 3-pointer behind the screen.

**Defensive Response:**
**Under Coverage.** The defender attempts to go under the screen to cut off the drive but fails to contest the shot.

**Result:** Made 3-Pointer.

**Coaching Note:**
Against "under" coverage, the ball handler must be willing to shoot behind the screen to punish the defense for giving up space.
`;

// Build comprehensive system prompt with ONLY Brown Bears context
const buildBrownSystemPrompt = (gameData) => {
  const { gameMetadata, keyMoments, playerAnalysis, tacticalBreakdown, ivyLeagueContext, patternPredictions } = gameData || {};

  return `You are "The Wire" Basketball Intelligence Assistant - an elite analyst EXCLUSIVELY for Brown Bears Men's Basketball.

**IMPORTANT CONTEXT:** You have been provided with COMPLETE SCOUTING DATA below. This is NOT a request to watch a video - all the video analysis has ALREADY BEEN DONE and the results are provided below. When users ask about timestamps like [00:22], refer to the play-by-play breakdown data provided in this prompt.

You have COMPLETE ACCESS to:
- Brown Bears offensive scheme analysis
- PRE-ANALYZED play-by-play video breakdown with timestamps (10 plays documented below)
- Player biomechanical and tactical assessments
- Ball screen coverage recommendations
- Ivy League season context and standings
- LLM-enhanced pattern predictions

**CRITICAL: You ONLY have data about Brown Bears Men's Basketball (Ivy League). You do NOT have any information about Real Madrid, FC Barcelona, EuroLeague, the Cleveland Browns (NFL), or any other teams. If asked about other teams, politely say you only have Brown Bears basketball data and redirect to Brown Bears analysis.**

---

## TEAM CONTEXT: Brown Bears Men's Basketball
**Season:** 2025-26 | **Record:** 7-10 (1-3 Ivy)
**Coach:** Mike Martin (13th season)
**Home:** Pizzitola Sports Center, Providence, RI
**Offensive Identity:** Ball-screen heavy, 4-Out/5-Out motion
**Offensive Rating:** 105.4 (7th in Ivy)

---

## PERSONNEL BREAKDOWN

### PRIMARY BALL HANDLERS

#### #3 Jeremah Jenkins | G | 6'2" | SO
- **Role:** Primary initiator, floor general
- **Stats:** 8.9 PPG, 5.4 APG (team leader), 34.7% FG, 47.4% 3PT, 2.2 SPG
- **Strengths:** Excellent court vision, patient in PnR, elite pocket passing, great lob timing
- **Weaknesses:** Struggles finishing through contact, limited as scorer
- **DEFENSIVE KEY:** Force left, make him score over help
- **VIDEO EVIDENCE:** See plays at [00:15], [00:22], [01:28], [06:50], [09:21]

#### #1 Isaiah Langham | G | 6'5" | FR
- **Role:** Secondary creator, emerging scorer
- **Stats:** 10.5 PPG, 42.2% FG
- **Profile:** Highest-rated recruit (52nd nationally), 1,000+ career HS points
- **Strengths:** Can score at all three levels, dangerous in transition
- **DEFENSIVE KEY:** Physical on screens, no clean looks in transition
- **VIDEO EVIDENCE:** See play at [00:09]

#### #10 Kino Lilly Jr. | G | Brown
- **Role:** Ball handler, secondary initiator
- **Strengths:** Excellent at reading defenses, screen rejection, handles blitz pressure well
- **VIDEO EVIDENCE:** See plays at [00:29], [01:03], [05:32], [07:34]

### INTERIOR THREATS

#### #45 N'famara Dabo | F/C | 6'8" | JR
- **Role:** Rim-runner, interior finisher, shot blocker, PRIMARY ROLL MAN
- **Stats:** 10.2 PPG, 7.2 RPG (team leader), 77.3% FG (ELITE), 1.9 BPG, 0 3PT attempts
- **Strengths:** Elite roll threat, excellent hands, anchor defender, great lob catcher
- **CRITICAL:** DROP COVERAGE IS VIABLE - he CANNOT shoot from mid-range
- **DEFENSIVE KEY:** Verticality at rim, no easy dunks
- **VIDEO EVIDENCE:** Finishes at [00:09], [00:15], [00:22], [01:28] - All alley-oops/layups

#### #31 (Big Man) | F/C
- **Role:** DHO screener, secondary roller
- **VIDEO EVIDENCE:** See plays at [06:50], [07:34] - Rolls for layups off DHO

#### #1 Landon Lewis | F | 6'7" | SR
- **Role:** Versatile scorer, experienced leader
- **Stats:** Variable PPG (26 vs Columbia OT), 47.6% FG (57.3% last year)
- **Strengths:** Can post smaller defenders, clutch performer, 22/23 starts last year
- **DEFENSIVE KEY:** Don't let him get deep position, contest early

### PERIMETER SHOOTERS

#### #10 Brady Loughlin | G | 6'5" | FR
- **Role:** Catch-and-shoot specialist
- **Stats:** 38.1% 3PT (8/21), 40%+ 3PT all 4 HS years
- **BEST SHOOTER on team** - primary spacing threat
- **DEFENSIVE KEY:** Close out under control, contest with length

#### #5 Adrian Uchidiuno | G | 6'2" | SO
- **Role:** Energy guard, bench scorer
- **Stats:** 10.9 PPG, 32.5% FG (INEFFICIENT), 28.6% 3PT
- **Profile:** High volume, streaky shooter
- **DEFENSIVE KEY:** Let him shoot contested, help off him

---

## OFFENSIVE SCHEME ANALYSIS

### Core System: 4-Out / 5-Out Motion
**Philosophy (Mike Martin):**
- Less pattern-oriented, more random
- Continuity ball screens over rigid set plays
- Matchup-driven decisions
- Harder to scout due to motion concepts

### Key Actions & Frequency (With Video Evidence)
| Action | Frequency | Efficiency | Video Timestamps |
|--------|-----------|------------|------------------|
| High Pick & Roll | HIGH | High | [00:09], [00:15], [01:03], [01:28], [09:21] |
| Transition Drag Screen | HIGH | High | [00:22] |
| DHO Series | MEDIUM | High | [06:50] |
| Chicago Action (Pindown→DHO) | MEDIUM | High | [07:34] |
| Screen Rejection | MEDIUM | High | [00:29] |

### Transition Offense
- Pushes pace after defensive rebounds
- Seeks early 3s in semi-transition
- Drag screen immediately if numbers aren't advantageous
- See [00:22] for transition alley-oop execution

---

${BROWN_VIDEO_ANALYSIS_DOCS}

---

## BALL SCREEN COVERAGE RECOMMENDATIONS

### By Handler:
| Handler | Coverage | Rationale | Video Evidence |
|---------|----------|-----------|----------------|
| Jenkins | Soft Hedge/Drop | Limited finishing, but PUNISHES with lobs | [00:15], [00:22], [01:28] |
| Langham | Switch/Hard Hedge | Athletic, precise pocket passer | [00:09] |
| Lilly | ICE/Switch | Great at rejection, handles traps | [00:29], [01:03] |

### By Screener:
| Screener | Coverage | Rationale |
|----------|----------|-----------|
| Dabo (#45) | DROP | Can't shoot, but elite roller - verticality required |
| #31 | SOFT | DHO threat, rolls well |
| Lewis | SOFT/SWITCH | More versatile, can face up |

---

## IVY LEAGUE CONTEXT

### Recent Results:
| Date | Opponent | Result | Notes |
|------|----------|--------|-------|
| 1/5 | Yale | L 70-53 | Poor shooting |
| 1/10 | Penn | L 81-73 | Length disrupted actions |
| 1/17 | Columbia | W 86-80 OT | Lewis 26 pts |
| 1/19 | Cornell | L 89-67 | Outscored |

### Key Stats (Ivy Rank):
- PPG: 72.6 (8th)
- FG%: 44.4% (6th)
- 3PT%: 33.7% (8th)
- ORTG: 105.4 (7th)
- BPG: 4.29 (1st) - Strong interior D

---

## PATTERN PREDICTIONS

### High-Probability Sequences (From Video):
1. High PnR → Pocket Pass to Roller → Finish at rim [00:09], [00:15]
2. Transition → Drag Screen → Alley-oop to Dabo [00:22]
3. PnR → Screen Rejection → Baseline Drive → Dump-off [00:29]
4. Blitz → Retreat → Short Roll Pass → 4-on-3 [01:03]
5. Spread PnR → Lob over Drop Coverage [01:28]
6. DHO → Tight Curl → Drive → Roller Finish [06:50]
7. Chicago Action → Pindown → DHO → Drive → Roller [07:34]
8. PnR → Under Coverage → Pull-up 3 [09:21]

### Vulnerability Windows:
- First 8 seconds: Predictable entries
- After made baskets: Slower pace
- Late clock: ISO tendencies
- Against press: Only Jenkins can handle

---

## DEFENSIVE GAME PLAN (FOR OPPONENTS)

### Primary Objectives:
1. **PACK THE PAINT** - Dabo is only interior threat, can't shoot
2. **FORCE CONTESTED 3s** - 32.6% shooting, let them fire with hand up
3. **DENY TRANSITION** - Best offense in semi-transition (see [00:22])
4. **PHYSICAL ON SCREENS** - Disrupt Jenkins/Langham timing
5. **TAG THE ROLLER** - Dabo finishes everything at rim

### What Brown Struggles Against:
- Physical, switching defenses
- Teams with length at guard positions
- Opponents who pack paint and dare them to shoot

### What Brown Succeeds Against:
- Drop coverage allowing pull-ups [09:21]
- Drop coverage allowing lobs [00:22], [01:28]
- Blitz they can beat with short rolls [01:03]
- Ice coverage they can reject [00:29]

---

## HARVARD DEFENSIVE SCOUTING — ATTACKING HARVARD'S DEFENSE

### 1. ATTACKING HARVARD'S SWITCHING DEFENSE

Harvard switches all ball screens. This creates two mismatches every time.

**On-Ball Switch Attacks:**
- **Jenkins/Langham vs Harvard Big:** ISO the big in space — Harvard bigs can't guard Brown's guards laterally. Quick crossover or hesitation to get downhill.
- **Dabo vs Harvard Guard:** Dabo (6'8", 77.3% FG) immediately seals the smaller guard. Duck-in post-up with deep position.
- **DHO with Re-Screen:** First DHO forces switch, then re-screen to force a SECOND switch back — scrambles assignments.
- **Ghost Screen → Drive:** Fake screen, Harvard switches on air, ball handler has open lane.
- **Transition Drag Screen:** Push pace — Harvard's switch communication breaks down in transition. Drag screen into lob (see [00:22]).

**Off-Ball Switch Attacks:**
- **Cross Screens for Dabo:** Cross screen in lane. After switch, Dabo's man is a guard sealed under basket. Lob or high-low.
- **Elevator Screens:** Two bigs at elbows, guard cuts between. Bigs close like elevator doors.
- **Pin-Down + Flare for Loughlin:** If they switch, Loughlin's new defender is a slower big — curl for mid-range or drive.
- **Chicago Action:** Brown's specialty (see [07:34]). Pindown → DHO forces TWO switches. Scrambles assignments.

### 2. ATTACKING HARVARD'S DIAMOND / 2-1-2 ZONE

Harvard's zone: Center at FT line, 1 guard top, 2 guards on wings, 1 forward under basket.

**Key Vulnerabilities:**
- High post / FT line area (center can't cover both directions)
- Short corners (forward under basket can't cover both sides)
- Corner 3s (wing guards can't cover wing AND corner)
- Ball reversal (top guard alone, slow to shift)

**Zone Beater Plays:**
- **"Fist Down"** (1-4 High): Flash to FT line, hi-lo to block → Dabo at rim
- **"Corner"** (Horns): Ball reversal, skip to corner → Loughlin for 3
- **"Short"** (Motion): Overload short corner, flash opposite → Lewis/Dabo
- **"Gap"** (Spread): Dribble penetrate wing gap, kick → Loughlin corner 3

**Concepts:** Overload short corner, High-Low action, Ball reversal + skip pass (2-3 quick reversals opens weak-side corner), Wing penetration by Langham (6'5").

### 3. ATTACKING HARVARD'S ZONE OOB DEFENSE

**BLOB (Baseline Out of Bounds):**
- **"Box Zipper":** Box formation. Dabo screens across zone for Lewis (cuts ball-side block). Loughlin zippers from weak-side elbow to 3PT line. Reads: Lewis on block for layup, Loughlin pop for 3, or Dabo seals for lob.
- **"Stack":** Stack all 4 ball-side block. Two break to corners, one flashes high. Creates zone confusion.
- **"Screen the Zone":** Screen the zone POSITION (not man). Dabo screens under-basket forward's area, cutter flashes to rim.

**SLOB (Sideline Out of Bounds):**
- **"Line":** 4 across FT line extended. Split to corners and top. Quick pass to middle of zone.

**Key Principle:** First pass must go to MIDDLE of zone — every defender is equidistant, can't all close out.

### 4. ADDITIONAL OFFENSIVE IDEAS VS HARVARD
- Push transition before zone sets (RUN EVERY POSSESSION)
- Crash offensive glass during man→zone transitions (1-2 sec confusion window)
- 5-Out alignment stretches Diamond zone to breaking point
- Let Jenkins read & react in spread (5.4 APG, 47.4% 3PT)
- Call timeout immediately when Harvard switches to zone
- Ball screen at TOP of Diamond zone — zone guard can't hedge like man

### GAME PLAN PRIORITY ORDER:
1. Push pace — transition before zone sets → Easy baskets, lobs
2. Hunt mismatches after switches — Dabo post-up, Jenkins ISO → High-% rim/FT looks
3. Overload short corner + skip to Loughlin → Open corner 3s
4. High-low action (FT line to block) → Dabo rim finishes
5. Zone OOB "Box Zipper" BLOB → Quick-hitter scores
6. Ball reversal (2-3 quick passes) → Weak-side open shots

---

## YOUR ROLE

You are a basketball analyst assistant. When users ask questions:
1. **ALWAYS refer to the play-by-play breakdown data provided above** - DO NOT say you cannot watch videos
2. Reference specific timestamps from the documented plays (e.g., [00:22] = Jenkins transition alley-oop)
3. Provide personnel insights, tactical analysis, and coverage recommendations
4. Be concise but thorough
5. **When asked about attacking Harvard's defense**, reference the Harvard Defensive Scouting section above with specific plays, concepts, and personnel matchups

**IMPORTANT:** All video analysis has ALREADY been completed. The 10 plays documented above are your source of truth. When asked "what happens at [00:22]?" answer from the PLAY 3 data provided above. You also have COMPLETE Harvard defensive scouting data including switching defense attacks, Diamond zone beaters, and zone OOB plays.

Remember: You ONLY know about Brown Bears Men's Basketball and the Harvard Crimson defensive scouting report. Never mention other unrelated teams.`;
};

// Call Gemini API with Brown context
const callBrownGeminiAPI = async (userMessage, conversationHistory, gameData) => {
  try {
    const systemPrompt = buildBrownSystemPrompt(gameData);

    const messages = [
      { role: 'user', content: systemPrompt },
      { role: 'assistant', content: 'I have complete access to the Brown Bears offensive analysis data you provided, including the 10 pre-analyzed plays with timestamps [00:09] through [09:21]. I can reference specific plays like Jenkins\' transition alley-oop at [00:22] or Lilly\'s Chicago action at [07:34]. I also have personnel assessments, ball screen coverage recommendations, and Ivy League context. What would you like to know about Brown?' },
      ...conversationHistory.filter(m => m.role !== 'system').map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content
      })),
      { role: 'user', content: userMessage }
    ];

    const response = await fetch(`${NODE_BACKEND_URL}/api/chat/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages,
        options: {
          model: 'gemini-2.5-flash',
          temperature: 0.7,
          sport: 'NCAA',
          teamId: 'brownBears',
          teamName: "Brown Bears Men's Basketball",
          passthrough: true, // Use full message context, don't override system prompt
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.content || data.response || data.message || data.text || 'I apologize, but I could not generate a response. Please try again.';
  } catch (error) {
    console.error('Gemini API error:', error);
    return `I'm currently unable to connect to the AI service. Based on Brown Bears data:\n\n${generateBrownFallbackResponse(userMessage)}`;
  }
};

// Fallback response generator for Brown
const generateBrownFallbackResponse = (question) => {
  const q = question.toLowerCase();

  if (q.includes('jenkins')) {
    return 'Jeremah Jenkins (#2) is Brown\'s primary initiator with 5.4 APG (team leader). He has excellent court vision but struggles finishing through contact (34.7% FG). DEFENSIVE KEY: Force left, make him score.';
  }
  if (q.includes('dabo')) {
    return 'N\'famara Dabo (#30) is an elite rim-runner with 77.3% FG but ZERO 3PT attempts. DROP COVERAGE IS VIABLE - he cannot punish from mid-range. Focus on verticality at the rim.';
  }
  if (q.includes('langham')) {
    return 'Isaiah Langham (#11) is the highest-rated recruit (52nd nationally) with 10.5 PPG. Can score at all three levels. DEFENSIVE KEY: Physical on screens, no clean looks in transition.';
  }
  if (q.includes('loughlin') || q.includes('shooter')) {
    return 'Brady Loughlin (#10) is their best shooter at 38.1% from three (40%+ all 4 HS years). Primary spacing threat - cannot leave him open.';
  }
  if (q.includes('coverage') || q.includes('defense') || q.includes('defend')) {
    return 'Against Brown: Use DROP coverage on Dabo (can\'t shoot), SOFT HEDGE on Jenkins (limited finisher). Pack the paint, force contested 3s (they shoot 32.6%), deny transition.';
  }
  if (q.includes('scheme') || q.includes('offense')) {
    return 'Brown runs 4-Out/5-Out motion with ball screen emphasis. Primary actions: Drag screens, Empty-side PnR, DHO series, Chicago action. They\'re harder to scout due to random/continuity concepts.';
  }
  return 'Brown Bears run a ball-screen heavy offense with 105.4 ORTG (7th Ivy). Key players: Jenkins (playmaker), Langham (scorer), Dabo (rim-runner). They struggle against physical switching defenses and length at guard.';
};

const BrownVideoChat = ({
  gameData,
  currentMarker,
  translations = {},
  onClose,
  isMinimized = false,
  onToggleMinimize,
}) => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: `**THE WIRE** - Brown Bears Basketball Intelligence\n\nI have **exclusive access** to Brown Bears offensive film analysis:\n\n**10 Timestamped Plays** - Detailed breakdowns from [00:09] to [09:21]\n**Personnel** - Jenkins (#3), Langham (#1), Lilly (#10), Dabo (#45)\n**Actions** - High PnR, DHO, Chicago Action, Screen Rejection\n**Defensive Responses** - Hard Hedge, Drop, Blitz, Ice coverage\n**Coverage Recommendations** - How to defend each handler/screener\n\nAsk me about specific plays, players, or defensive schemes. I'll reference exact timestamps from the video.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  // Auto-scroll to bottom
  useEffect(() => {
    if (shouldAutoScrollRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (!isMinimized) {
      inputRef.current?.focus();
    }
  }, [isMinimized]);

  // Add context message when marker changes
  useEffect(() => {
    if (currentMarker) {
      const contextMessage = {
        id: `context-${currentMarker.id}`,
        role: 'system',
        content: `Now viewing: **${currentMarker.event}** at ${currentMarker.timeVideo}\n\n_${currentMarker.analysis}_`,
      };
      shouldAutoScrollRef.current = false;
      setMessages((prev) => {
        if (prev.some((m) => m.id === contextMessage.id)) return prev;
        return [...prev, contextMessage];
      });
    }
  }, [currentMarker?.id]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessageText = input.trim();
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessageText,
    };

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await callBrownGeminiAPI(userMessageText, messages, gameData);
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Brown-specific suggested questions tied to video
  const suggestions = [
    "What happens at [00:22]?",
    "How does Brown beat blitz coverage?",
    "Analyze Jenkins vs drop coverage",
    "Explain the Chicago action at 7:34",
  ];

  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-black/90 backdrop-blur-md border border-white/[0.18] rounded-full shadow-lg hover:bg-black/95 transition-colors"
      >
        <Bot className="w-5 h-5 text-amber-600" />
        <span className="text-white text-sm font-medium">Ask about Brown</span>
        <ChevronUp className="w-4 h-4 text-white/60" />
      </button>
    );
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden flex flex-col h-full max-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.15] bg-black/40 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-700/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-amber-500" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Brown Bears Intel</h3>
            <p className="text-white/50 text-xs">Ask about the offense</p>
          </div>
        </div>
        {onToggleMinimize && (
          <button
            onClick={onToggleMinimize}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronDown className="w-4 h-4 text-white/60" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role !== 'user' && (
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                message.role === 'system' ? 'bg-blue-500/20' : 'bg-amber-700/30'
              }`}>
                {message.role === 'system' ? (
                  <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                ) : (
                  <Bot className="w-3.5 h-3.5 text-amber-500" />
                )}
              </div>
            )}
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                message.role === 'user'
                  ? 'bg-amber-700 text-white'
                  : message.role === 'system'
                  ? 'bg-blue-500/10 border border-blue-500/30 text-blue-100'
                  : 'bg-white/[0.08] text-white'
              } prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_strong]:text-amber-300 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_code]:bg-white/10 [&_code]:px-1 [&_code]:rounded`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
            />
            {message.role === 'user' && (
              <div className="w-7 h-7 rounded-lg bg-white/[0.12] flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-lg bg-amber-700/30 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <div className="bg-white/[0.08] px-3 py-2 rounded-xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-2.5 py-1 text-xs bg-white/[0.06] hover:bg-white/[0.12] text-white/70 hover:text-white rounded-lg transition-colors border border-white/[0.15]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-white/[0.15]">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={translations.chatPlaceholder || "Ask about Brown's offense..."}
            className="flex-1 px-3 py-2 bg-white/[0.06] border border-white/[0.18] rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-amber-500/50"
            disabled={isTyping}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="px-3 py-2 bg-amber-700 hover:bg-amber-600 disabled:bg-white/[0.08] disabled:text-white/40 text-white rounded-lg transition-colors"
          >
            {isTyping ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrownVideoChat;
